import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import localeData from 'dayjs/plugin/localeData'
import utc from 'dayjs/plugin/utc'
import logo_default from '@/assets/login/logoIcon.png'

// Import các locale bạn dùng
import 'dayjs/locale/vi'
import 'dayjs/locale/en'
import { useAppStore } from '@/store'

// Kích hoạt plugin
dayjs.extend(customParseFormat)
dayjs.extend(localeData)
dayjs.extend(utc)

const appStore = useAppStore()

const defaultFormats = [
  { key: 'vi-VN', value: 'DD/MM/YYYY hh:mm A' },
  { key: 'en-US', value: 'MMMM D, YYYY hh:mm A' },
]

export function getCurrentDate() {
  const date = new Date()
  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  return `${year}-${month}-${day}`
}

export function getTableRowIndex(pageSize: number, currentPage: number, rowIndex: number): number {
  if (currentPage === 0)
    currentPage = 1

  return (currentPage - 1) * pageSize + rowIndex + 1
}

export function transformMinioUrl(url: string) {
  if (url)
    return url.replace('http://113.20.107.188:9000/', 'https://api.nextschool.app/minio/')
  else
    return url
}

/**
 * Tìm định dạng theo locale từ mảng defaultFormats
 */
function getFormatByLocale(locale: string) {
  const found = defaultFormats.find(item => item.key === locale)
  if (found)
    return found.value
  return defaultFormats[0].value
}

/**
 * Định dạng ngày với hỗ trợ locale và format mặc định
 */
export function formatDate(input: any, isTimestamp = false, format?: any, locale = appStore.language || 'en-US') {
  let date = null
  if (isTimestamp)
    date = dayjs.unix(input).locale(locale)

  else
    date = dayjs(input).locale(locale)

  if (!date.isValid())
    return ''

  const outputFormat = format || getFormatByLocale(locale)
  return date.format(outputFormat)
}

export function formatNumber(value: number | string, toFixed = 0): string | number {
  if (value) {
    const val = Number(value).toFixed(toFixed).replace('.', ',')
    return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  }
  else if (value === 0 || value === '0') {
    return 0
  }
  return ''
}

export function formatByte(value: number, toFixed = 0, isUnit = true): string | number {
  if (value) {
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let i = 0
    let val = value
    while (val >= 1024 && i < units.length - 1) {
      val /= 1024
      i++
    }
    if (isUnit)
      return `${Number(val).toFixed(toFixed)} ${units[i]}`

    return `${Number(val).toFixed(toFixed)}`
  }
  else if (value === 0) {
    return 0
  }
  return ''
}

export function getDefaultAvatarAgent(url: string) {
  if (url)
    return url
  else
    return 'https://api.nextschool.app/minio/default-avatar-en.png'
}

export function generateAgentAvatar(agentName: string) {
  // Generate a consistent color based on agent name
  const colors = ['#764ab9', '#9571c4', '#5c3a95', '#a480e2', '#3b82f6', '#60a5fa']
  const colorIndex = agentName.split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0) % colors.length
  const backgroundColor = colors[colorIndex]
  const textColor = '#ffffff'

  // Get initials from agent name
  const initials = agentName
    .split(' ')
    .map((word: string) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  // return `https://ui-avatars.com/api/?name=${initials}&background=${backgroundColor.slice(1)}&color=${textColor.slice(1)}&size=90&bold=true&format=svg`
  return logo_default
}

export function handleToCamelCase(text = '', snake: any): string {
  if (!snake)
    return ''

  const camel = snake.replace(/[-_]+([a-z])/g, (_: any, letter: any) => letter.toUpperCase())
  return text + camel
}

export function generateGradientFromColor(color1: string, color2?: string): string {
  if (color2) {
    // If two colors are provided, use them directly
    return `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`
  }

  // Original logic for single color (backward compatibility)
  // Convert hex to HSL for better gradient generation
  const hex = color1.replace('#', '')
  const r = parseInt(hex.substr(0, 2), 16) / 255
  const g = parseInt(hex.substr(2, 2), 16) / 255
  const b = parseInt(hex.substr(4, 2), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h; let s; const l = (max + min) / 2

  if (max === min) {
    h = s = 0
  }
  else {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break
      case g: h = (b - r) / d + 2; break
      case b: h = (r - g) / d + 4; break
      default: h = 0
    }
    h /= 6
  }

  // Enhance contrast by increasing saturation and adjusting lightness
  const enhancedSaturation = Math.min(s * 1.5, 1) // Increase saturation up to max value
  const lightness1 = Math.min(l * 1.3, 0.9) // Make the lighter color more intense
  const lightness2 = Math.max(l * 0.7, 0.1) // Make the darker color more intense

  const hslToHex = (h: number, s: number, l: number): string => {
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0)
        t += 1
      if (t > 1)
        t -= 1
      if (t < 1 / 6)
        return p + (q - p) * 6 * t
      if (t < 1 / 2)
        return q
      if (t < 2 / 3)
        return p + (q - p) * (2 / 3 - t) * 6
      return p
    }

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    const r = hue2rgb(p, q, h + 1 / 3)
    const g = hue2rgb(p, q, h)
    const b = hue2rgb(p, q, h - 1 / 3)

    const toHex = (c: number): string => {
      const hex = Math.round(c * 255).toString(16)
      return hex.length === 1 ? `0${hex}` : hex
    }

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
  }

  const generatedColor1 = hslToHex(h, enhancedSaturation, lightness1)
  const generatedColor2 = hslToHex(h, enhancedSaturation, lightness2)

  return `linear-gradient(135deg, ${generatedColor1} 0%, ${generatedColor2} 100%)`
}

export function extractColorsFromGradient(gradient: string): { color1: string; color2: string } {
  // Extract colors from gradient string
  const colorMatches = gradient.match(/#[a-fA-F0-9]{6}/g)

  if (!colorMatches || colorMatches.length < 2) {
    // If can't extract 2 colors, return default colors
    return {
      color1: '#667eea',
      color2: '#764ba2',
    }
  }

  return {
    color1: colorMatches[0],
    color2: colorMatches[1],
  }
}

/**
 * Remove Vietnamese diacritics (accents) from a string
 * @param str - The input string
 * @returns The string without Vietnamese diacritics
 */
export function removeVietnameseDiacritics(str: string): string {
  if (!str)
    return ''
  return String(str)
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
}

export function filterOptionVietnamese(pattern: string, option: any) {
  if (!pattern)
    return true
  const normalizedPattern = removeVietnameseDiacritics(pattern.toLowerCase())
  const normalizedName = removeVietnameseDiacritics((option.name || '').toLowerCase())
  return normalizedName.includes(normalizedPattern)
}

export function formatMoney(value: number, toFixed = 1) {
  if (value) {
    const units = ['', 'K', 'M', 'B', 'T'] // thousand, million, billion, trillion
    let i = 0
    let val = value

    while (val >= 1000 && i < units.length - 1) {
      val /= 1000
      i++
    }

    // Truncate thay vì round
    const factor = 10 ** toFixed
    const truncated = Math.floor(val * factor) / factor

    // Xóa .0 nếu có
    return `${truncated.toString().replace(/\.0$/, '')}${units[i]}`
  }

  if (value === 0)
    return '0'
  return ''
}
